import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const PurchaseVoucherContext = createContext();
export const usePurchaseVoucher = () => useContext(PurchaseVoucherContext);

export const PurchaseVoucherProvider = ({ children }) => {
  const { token, isReady, logout } = useAuth();

  const [purchaseVouchers, setPurchaseVouchers] = useState([]);
  const [loading, setLoading] = useState(false);

  const mountedRef = useRef(true);

  const baseURL = BASE_URL.replace(/\/$/, "");
  const ENDPOINT = "/purchase-vouchers";

  /* ---------------- MOUNT SAFETY ---------------- */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetPurchaseVouchers = (value) => {
    if (mountedRef.current) setPurchaseVouchers(value);
  };

  /* ---------------- FETCH ---------------- */
  const fetchPurchaseVouchers = useCallback(async () => {
    if (!token) return;

    setLoading(true);

    try {
      const res = await fetch(`${baseURL}${ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(payload?.message || "Fetch failed");
      }

      const items = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      safeSetPurchaseVouchers(items);

      return payload;
    } catch (err) {
      console.error("[PurchaseVoucher] fetch error:", err);

      if (err.message?.toLowerCase().includes("token")) {
        logout?.();
      }

      safeSetPurchaseVouchers([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL, logout]);

  /* ---------------- CREATE ---------------- */
  const createPurchaseVoucher = useCallback(
    async (voucherData) => {
      try {
        const isFormData =
          typeof FormData !== "undefined" &&
          voucherData instanceof FormData;

        const res = await fetch(`${baseURL}${ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            ...(isFormData
              ? {}
              : { "Content-Type": "application/json" }),
          },
          body: isFormData
            ? voucherData
            : JSON.stringify(voucherData),
        });

        const payload = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(payload?.message || "Create failed");
        }

        await fetchPurchaseVouchers();
        return payload;
      } catch (err) {
        console.error("[PurchaseVoucher] create error:", err);
        throw err;
      }
    },
    [token, baseURL, fetchPurchaseVouchers]
  );

  /* ---------------- OPTIONAL: DELETE (missing earlier) ---------------- */
  const deletePurchaseVoucher = useCallback(async (id) => {
    try {
      const res = await fetch(`${baseURL}${ENDPOINT}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(payload?.message || "Delete failed");

      safeSetPurchaseVouchers((prev) =>
        prev.filter((v) => String(v.id) !== String(id))
      );

      return payload;
    } catch (err) {
      console.error("[PurchaseVoucher] delete error:", err);
      throw err;
    }
  }, [token, baseURL]);

  /* ---------------- INIT ---------------- */
  useEffect(() => {
    if (!isReady || !token) return;

    fetchPurchaseVouchers().catch(() => {});
  }, [isReady, token, fetchPurchaseVouchers]);

  return (
    <PurchaseVoucherContext.Provider
      value={{
        purchaseVouchers,
        loading,
        fetchPurchaseVouchers,
        createPurchaseVoucher,
        deletePurchaseVoucher,
      }}
    >
      {children}
    </PurchaseVoucherContext.Provider>
  );
};